/**
 * Funtroo theme behaviour.
 *
 * Product/collection rendering now happens server-side in Liquid, so this file
 * only carries presentational behaviour plus the AJAX cart. Cart state lives in
 * Shopify's cart, never in localStorage.
 */

/* ---------------------------------------------------------------- Cart drawer */

function openCartDrawer() {
  const overlay = document.querySelector('.cart-drawer-overlay');
  if (!overlay) return;
  overlay.classList.add('active');
  // Also held on <body>: refreshCartDrawer() replaces the section's innerHTML,
  // which destroys the overlay and any class sitting on it.
  document.body.classList.add('cart-open');
  document.body.style.overflow = 'hidden';
}

function closeCartDrawer() {
  const overlay = document.querySelector('.cart-drawer-overlay');
  if (!overlay) return;
  overlay.classList.remove('active');
  document.body.classList.remove('cart-open');
  document.body.style.overflow = 'auto';
}

/**
 * Re-render the drawer from the server via the Section Rendering API so the
 * markup stays a single source of truth in cart-drawer.liquid.
 */
async function refreshCartDrawer({ open = false } = {}) {
  const wasOpen = document.querySelector('.cart-drawer-overlay')?.classList.contains('active');

  const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
  const data = await res.json();
  const markup = data['cart-drawer'];
  if (!markup) return;

  const host = document.getElementById('shopify-section-cart-drawer');
  if (host) {
    // The response includes the section wrapper, so unwrap one level.
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const fresh = parsed.getElementById('shopify-section-cart-drawer');
    host.innerHTML = fresh ? fresh.innerHTML : markup;
  }

  // Header badges live outside the section.
  const cart = await (await fetch(window.FNTROO.routes.cart + '.js')).json();
  document.querySelectorAll('.cart-count').forEach((el) => {
    el.textContent = cart.item_count;
  });

  bindCartDrawer();
  if (open || wasOpen) openCartDrawer();
}

async function changeCartLine(key, quantity) {
  await fetch(`${window.FNTROO.routes.cart_change}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  await refreshCartDrawer();
}

function bindCartDrawer() {
  const overlay = document.querySelector('.cart-drawer-overlay');
  if (!overlay) return;

  overlay.querySelector('.cart-close-btn')?.addEventListener('click', closeCartDrawer);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeCartDrawer();
  });

  overlay.querySelectorAll('[data-action="increase"], [data-action="decrease"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      changeCartLine(btn.dataset.key, parseInt(btn.dataset.qty, 10));
    });
  });

  overlay.querySelectorAll('[data-action="remove"]').forEach((btn) => {
    btn.addEventListener('click', () => changeCartLine(btn.dataset.key, 0));
  });
}

/* ------------------------------------------------------------ Add to cart */

async function addToCart(variantId, quantity = 1) {
  const res = await fetch(`${window.FNTROO.routes.cart_add}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity }] })
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    console.error('Add to cart failed:', err);
    alert(err.description || 'Could not add this item to your cart.');
    return false;
  }

  await refreshCartDrawer({ open: true });
  return true;
}

/* ---------------------------------------------------------------- Bindings */

document.addEventListener('DOMContentLoaded', () => {
  // Cart trigger in the header opens the drawer rather than navigating to /cart.
  const cartTrigger = document.querySelector('.cart-trigger');
  if (cartTrigger) {
    cartTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      openCartDrawer();
    });
  }

  bindCartDrawer();

  // Product forms submit through AJAX so the drawer can open in place.
  document.querySelectorAll('form[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const id = form.querySelector('[name="id"]')?.value;
      if (!id) return;

      const btn = form.querySelector('[type="submit"]');
      const original = btn?.textContent;
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'ADDING...';
      }

      await addToCart(id, 1);

      if (btn) {
        btn.disabled = false;
        btn.textContent = original;
      }
    });
  });

  // Scroll-to-top control.
  const scrollBtn = document.querySelector('.scroll-top-btn');
  if (scrollBtn) {
    window.addEventListener('scroll', () => {
      scrollBtn.style.display = window.scrollY > 600 ? 'flex' : 'none';
    });
    scrollBtn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }
});

window.FntrooCart = { open: openCartDrawer, close: closeCartDrawer, add: addToCart };

/* ------------------------------------------------------- Product detail page */

function initProductDetail() {
  const form = document.querySelector('form[data-product-form]');
  if (!form) return;

  const idInput = form.querySelector('[name="id"]');
  const warning = document.getElementById('pdp-size-warning');
  const sizeButtons = form.querySelectorAll('.size-btn');

  sizeButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.dataset.available !== 'true') return;
      sizeButtons.forEach((b) => b.classList.remove('selected'));
      btn.classList.add('selected');
      idInput.value = btn.dataset.variantId;
      if (warning) warning.style.display = 'none';
    });
  });

  // Block submit until a size is chosen; funtroo.js's global handler does the add.
  form.addEventListener(
    'submit',
    (e) => {
      if (idInput.value) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!warning) return;
      warning.style.display = 'block';
      warning.style.animation = 'none';
      void warning.offsetHeight; // reflow so the shake replays
      warning.style.animation = 'pdpShake 0.4s ease';
    },
    true // capture: run before the AJAX submit handler
  );
}

function initDescriptionToggle() {
  const text = document.getElementById('pdp-desc-text');
  const toggle = document.getElementById('pdp-desc-toggle');
  if (!text || !toggle) return;

  const LIMIT = 180;
  const full = text.textContent.trim();
  if (full.length <= LIMIT) return;

  const truncated = full.slice(0, LIMIT).trimEnd() + '…';
  let expanded = false;

  text.textContent = truncated;
  toggle.style.display = 'inline';
  toggle.textContent = 'MORE';

  toggle.addEventListener('click', () => {
    expanded = !expanded;
    text.textContent = expanded ? full : truncated;
    toggle.textContent = expanded ? 'LESS' : 'MORE';
  });
}

function initAccordions() {
  document.querySelectorAll('.accordion-trigger').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      trigger.closest('.accordion-item')?.classList.toggle('active');
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductDetail();
  initDescriptionToggle();
  initAccordions();
});

/* ------------------------------------------------------------ Promo banner */

function initPromoBanner() {
  const overlay = document.getElementById('promoPopupOverlay');
  if (!overlay) return;

  const slides = overlay.querySelectorAll('[data-promo-slide]');
  if (!slides.length) return;

  // Once per browser session, matching the original behaviour.
  try {
    if (sessionStorage.getItem('fntroo_promo_banner_shown')) return;
    sessionStorage.setItem('fntroo_promo_banner_shown', '1');
  } catch (e) {
    // Private mode / storage blocked: show it rather than failing closed.
  }

  const chosen = slides[Math.floor(Math.random() * slides.length)];
  chosen.hidden = false;
  overlay.hidden = false;
  overlay.classList.add('is-visible');

  const close = () => {
    overlay.classList.remove('is-visible');
    overlay.hidden = true;
  };

  document.getElementById('promoPopupClose')?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    close();
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

/* ------------------------------------------------- Collection filter + sort */

function initCatalog() {
  const grid = document.querySelector('.catalog-grid');
  if (!grid) return;

  const cards = [...grid.querySelectorAll('.product-card')];
  const countText = document.getElementById('item-count-text');
  const filters = { size: '', type: '', color: '', sort: 'default' };

  function apply() {
    let visible = cards.filter((card) => {
      if (filters.size) {
        const sizes = (card.dataset.sizes || '').split(',');
        if (!sizes.includes(filters.size.toUpperCase())) return false;
      }
      if (filters.type && !(card.dataset.type || '').includes(filters.type)) return false;
      if (filters.color && card.dataset.color !== filters.color) return false;
      return true;
    });

    const num = (c) => parseInt(c.dataset.price, 10) || 0;
    const str = (c) => (c.dataset.title || '').toLowerCase();

    if (filters.sort === 'price-asc') visible.sort((a, b) => num(a) - num(b));
    else if (filters.sort === 'price-desc') visible.sort((a, b) => num(b) - num(a));
    else if (filters.sort === 'title-asc') visible.sort((a, b) => str(a).localeCompare(str(b)));
    else if (filters.sort === 'title-desc') visible.sort((a, b) => str(b).localeCompare(str(a)));

    cards.forEach((c) => { c.style.display = 'none'; });
    visible.forEach((c) => {
      c.style.display = '';
      grid.appendChild(c); // re-append to apply sort order
    });

    if (countText) countText.textContent = `${visible.length} item${visible.length === 1 ? '' : 's'}`;

    let empty = grid.querySelector('.catalog-empty');
    if (!visible.length) {
      if (!empty) {
        empty = document.createElement('div');
        empty.className = 'container-full text-center catalog-empty';
        empty.style.cssText = 'padding: 100px 0; grid-column: 1 / -1;';
        empty.innerHTML = '<span class="mono" style="color: var(--text-secondary);">NO PRODUCTS FOUND MATCHING FILTERS</span>';
        grid.appendChild(empty);
      }
    } else if (empty) {
      empty.remove();
    }
  }

  document.querySelectorAll('.filter-dropdown-option').forEach((opt) => {
    opt.addEventListener('click', () => {
      const key = opt.dataset.filter;
      if (!(key in filters)) return;

      filters[key] = opt.dataset.value;
      opt
        .closest('.filter-dropdown')
        ?.querySelectorAll('.filter-dropdown-option')
        .forEach((o) => o.classList.remove('selected'));
      opt.classList.add('selected');

      opt.closest('.filter-item')?.classList.remove('open');
      apply();
    });
  });

  // Dropdown open/close
  document.querySelectorAll('.filter-item').forEach((item) => {
    item.addEventListener('click', (e) => {
      if (e.target.closest('.filter-dropdown-option')) return;
      const wasOpen = item.classList.contains('open');
      document.querySelectorAll('.filter-item').forEach((i) => i.classList.remove('open'));
      if (!wasOpen) item.classList.add('open');
      e.stopPropagation();
    });
  });
  document.addEventListener('click', () => {
    document.querySelectorAll('.filter-item').forEach((i) => i.classList.remove('open'));
  });

  // Quick add straight from the grid
  grid.addEventListener('click', async (e) => {
    const btn = e.target.closest('.quick-add-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = '…';
    await addToCart(btn.dataset.variantId, 1);
    btn.disabled = false;
    btn.textContent = original;
  });

  apply();
}

document.addEventListener('DOMContentLoaded', () => {
  initPromoBanner();
  initCatalog();
});

/* ----------------------------------------------------------------- Homepage */

function initHomepage() {
  const slides = document.querySelectorAll('.scroll-slide');
  const backgrounds = document.querySelectorAll('.homepage-bg-image');
  if (!slides.length || !backgrounds.length) return;

  // Each slide activates the matching fixed wallpaper as it scrolls into view.
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = [...slides].indexOf(entry.target);
        if (index < 0) return;
        const target = Math.min(index + 1, backgrounds.length);
        backgrounds.forEach((bg, i) => bg.classList.toggle('active', i === target - 1));

        // The campaign overlay belongs to the hero only.
        const overlay = document.querySelector('.hero-campaign-overlay');
        if (overlay) overlay.style.opacity = entry.target.id === 'slide-5' ? '0' : '1';
      });
    },
    { threshold: 0.5 }
  );

  slides.forEach((s) => observer.observe(s));

  // Carousel arrows
  const track = document.querySelector('.carousel-track');
  if (track) {
    const step = () => Math.max(track.clientWidth * 0.8, 240);
    document
      .querySelector('.carousel-btn.prev-btn')
      ?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    document
      .querySelector('.carousel-btn.next-btn')
      ?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  }
}

document.addEventListener('DOMContentLoaded', initHomepage);

/* --------------------------------------------------- PDP gallery (side-scroll) */

function initProductGallery() {
  const wrap = document.querySelector('[data-gallery]');
  if (!wrap) return;

  const track = wrap.querySelector('.pdp-media-column');
  const prev = wrap.querySelector('.gallery-nav.prev');
  const next = wrap.querySelector('.gallery-nav.next');
  const dotsHost = wrap.querySelector('.gallery-dots');
  if (!track) return;

  const slides = [...track.children];

  // The Editorial skin leaves the column stacked vertically; controls only make
  // sense once it's actually a horizontal scroller.
  const isHorizontal = () => getComputedStyle(track).flexDirection === 'row';

  if (slides.length < 2 || !isHorizontal()) {
    dotsHost?.remove();
    prev?.remove();
    next?.remove();
    return;
  }

  prev.hidden = false;
  next.hidden = false;

  slides.forEach((_, i) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'gallery-dot';
    dot.setAttribute('aria-label', `Image ${i + 1} of ${slides.length}`);
    dot.addEventListener('click', () => {
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
    });
    dotsHost.appendChild(dot);
  });

  const dots = [...dotsHost.children];

  const currentIndex = () => {
    const mid = track.scrollLeft + track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    slides.forEach((s, i) => {
      const centre = s.offsetLeft - track.offsetLeft + s.clientWidth / 2;
      const d = Math.abs(centre - mid);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  };

  const sync = () => {
    const i = currentIndex();
    dots.forEach((d, n) => d.classList.toggle('active', n === i));
    prev.disabled = i === 0;
    next.disabled = i === slides.length - 1;
  };

  const go = (delta) => {
    const target = Math.min(Math.max(currentIndex() + delta, 0), slides.length - 1);
    track.scrollTo({ left: slides[target].offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };

  prev.addEventListener('click', () => go(-1));
  next.addEventListener('click', () => go(1));

  // rAF-throttled so the scroll handler never blocks a frame.
  let ticking = false;
  track.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { sync(); ticking = false; });
    },
    { passive: true }
  );

  wrap.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  });

  sync();
}

document.addEventListener('DOMContentLoaded', initProductGallery);

/* ----------------------------------------------------------- Mobile menu */

function initMobileMenu() {
  const drawer = document.getElementById('mobile-nav-drawer');
  if (!drawer) return;

  const open = () => {
    drawer.classList.add('active');
    document.body.style.overflow = 'hidden';
  };

  const close = () => {
    drawer.classList.remove('active');
    // The homepage owns body overflow for its scroll-snap container.
    if (!document.body.classList.contains('homepage-body')) {
      document.body.style.overflow = '';
    }
  };

  document.querySelectorAll('.mobile-nav-toggle button').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      open();
    });
  });

  drawer.querySelector('.mobile-drawer-close')?.addEventListener('click', close);

  // Tapping a link should close the drawer before the page changes.
  drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('active')) close();
  });
}

document.addEventListener('DOMContentLoaded', initMobileMenu);

/* ------------------------------------------------- Product page modals */

function initProductModals() {
  const openModal = (el) => {
    if (!el) return;
    el.classList.add('active');
    document.body.style.overflow = 'hidden';
  };

  const closeModal = (el) => {
    if (!el) return;
    el.classList.remove('active');
    document.body.style.overflow = '';
  };

  // Wire a trigger/overlay/close-button trio, including click-outside and Escape.
  const wire = (triggerSel, overlayId, closeId) => {
    const overlay = document.getElementById(overlayId);
    if (!overlay) return null;

    document.querySelectorAll(triggerSel).forEach((t) =>
      t.addEventListener('click', (e) => {
        e.preventDefault();
        openModal(overlay);
      })
    );

    document.getElementById(closeId)?.addEventListener('click', () => closeModal(overlay));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal(overlay);
    });
    return overlay;
  };

  const sizeChart = wire('#size-chart-trigger, .size-chart-trigger-btn', 'size-chart-overlay', 'sc-close-btn');
  const fitFinder = wire('#fit-finder-trigger, .fit-finder-trigger-btn', 'fit-finder-overlay', 'ff-close-btn');
  // Clear any previous recommendation when the finder is reopened.
  document.querySelectorAll('#fit-finder-trigger, .fit-finder-trigger-btn').forEach((t) =>
    t.addEventListener('click', () => document.getElementById('ff-result')?.classList.remove('active'))
  );
  const notifyMe  = wire('[data-notify-me]', 'notify-me-overlay', 'nm-close-btn');

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    [sizeChart, fitFinder, notifyMe].forEach((m) => {
      if (m?.classList.contains('active')) closeModal(m);
    });
  });

  /* Size chart: inches <-> centimetres */
  const unitButtons = document.querySelectorAll('.size-toggle-btn');
  unitButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const unit = btn.dataset.unit;
      unitButtons.forEach((b) => b.classList.toggle('active', b === btn));
      document.querySelectorAll('.size-chart-table td[data-inch]').forEach((td) => {
        td.textContent = unit === 'cm' ? td.dataset.cm : td.dataset.inch;
      });
    });
  });

  /* Fit finder */
  const ffForm = document.getElementById('fit-finder-form');
  if (ffForm) {
    ffForm.addEventListener('submit', (e) => {
      e.preventDefault();

      const height = parseFloat(document.getElementById('ff-height').value);
      const weight = parseFloat(document.getElementById('ff-weight').value);
      const style = document.getElementById('ff-style').value;

      let size = 'M';
      if (height < 165) {
        size = weight < 60 ? 'S' : 'M';
      } else if (height < 178) {
        if (weight < 65) size = 'S';
        else if (weight < 78) size = 'M';
        else size = 'L';
      } else if (height < 188) {
        if (weight < 75) size = 'M';
        else if (weight < 90) size = 'L';
        else size = 'XL';
      } else {
        if (weight < 85) size = 'L';
        else if (weight < 105) size = 'XL';
        else size = '2XL';
      }

      // A standard fit means dropping one size out of the oversized cut.
      if (style === 'standard') {
        const scale = ['S', 'M', 'L', 'XL', '2XL'];
        const i = scale.indexOf(size);
        if (i > 0) size = scale[i - 1];
      }

      const val = document.getElementById('ff-result-val');
      const note = document.getElementById('ff-result-note');
      const result = document.getElementById('ff-result');

      if (val) val.textContent = size;
      if (note) {
        note.textContent =
          style === 'standard'
            ? `Size ${size} gives a cleaner profile rather than the dropped-shoulder boxy cut.`
            : `Size ${size} fits boxy with dropped shoulders — the intended oversized silhouette.`;
      }
      result?.classList.add('active');
    });
  }

  /* Restock request */
  const nmForm = document.getElementById('notify-me-form');
  if (nmForm) {
    nmForm.addEventListener('submit', (e) => {
      e.preventDefault();
      nmForm.hidden = true;
      const ok = document.getElementById('nm-success');
      if (ok) ok.hidden = false;
    });
  }

  /* Sold-out button opens the restock modal with the chosen size, if any. */
  document.querySelector('[data-notify-me]')?.addEventListener('click', () => {
    const chosen = document.querySelector('.size-btn.selected');
    const label = document.getElementById('nm-product-size');
    if (label) label.textContent = chosen ? `SIZE: ${chosen.textContent.trim()}` : 'ALL SIZES';
  });
}

document.addEventListener('DOMContentLoaded', initProductModals);

/* ------------------------------------------------- Homepage shoppable hotspots */

function initShoppableHotspots() {
  const overlay = document.querySelector('.hotspot-overlay');
  if (!overlay) return;

  // A single floating label follows the cursor and swaps its text per hotspot,
  // rather than one tooltip per hotspot - matches the original desktop behaviour.
  let cursorLabel = document.getElementById('hotspot-cursor-label');
  if (!cursorLabel) {
    cursorLabel = document.createElement('div');
    cursorLabel.id = 'hotspot-cursor-label';
    cursorLabel.className = 'hotspot-cursor-label mono';
    document.body.appendChild(cursorLabel);
  }

  const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (!isDesktop) return; // Touch devices rely on the hotspot's own tap-to-navigate link.

  document.querySelectorAll('.hotspot').forEach((hotspot) => {
    hotspot.addEventListener('mouseenter', () => {
      const name = hotspot.dataset.name || '';
      const tags = hotspot.dataset.tags || '';
      cursorLabel.innerHTML = `
        <div class="hotspot-title">${name.toUpperCase()}</div>
        <div class="hotspot-subtext">${tags.toUpperCase()}</div>
      `;
      cursorLabel.classList.add('active');
    });

    hotspot.addEventListener('mousemove', (e) => {
      cursorLabel.style.left = `${e.clientX + 15}px`;
      cursorLabel.style.top = `${e.clientY + 15}px`;
    });

    hotspot.addEventListener('mouseleave', () => {
      cursorLabel.classList.remove('active');
    });
  });
}

document.addEventListener('DOMContentLoaded', initShoppableHotspots);
